import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LuMicroscope, LuMonitorSmartphone, LuSchool } from "react-icons/lu";
import { PiTestTubeBold, PiBookOpenText, PiMusicNotesBold } from "react-icons/pi";
import { TbAtom } from "react-icons/tb";
import { RxDesktop } from "react-icons/rx";
import { GiSoccerBall } from "react-icons/gi";
import { FaBus } from "react-icons/fa";

const facilitiesData = [
  {
    title: "Biology Lab",
    description: "State-of-the-art biology laboratory for hands-on learning, specimens, and experiments.",
    path: "/facilities/biology-lab",
    icon: <LuMicroscope size={24} />,
    accentColor: "#22c55e",
    borderColor: "rgba(34,197,94,0.2)",
    glowColor: "rgba(34,197,94,0.12)",
  },
  {
    title: "Chemistry Lab",
    description: "Well-equipped chemistry laboratory with safety protocols and advanced apparatus.",
    path: "/facilities/chemistry-lab",
    icon: <PiTestTubeBold size={24} />,
    accentColor: "#3b82f6",
    borderColor: "rgba(59,130,246,0.2)",
    glowColor: "rgba(59,130,246,0.12)",
  },
  {
    title: "Physics Lab",
    description: "Modern physics laboratory with precision instruments for practical demonstrations.",
    path: "/facilities/physics-lab",
    icon: <TbAtom size={24} />,
    accentColor: "#f97316",
    borderColor: "rgba(249,115,22,0.2)",
    glowColor: "rgba(249,115,22,0.12)",
  },
  {
    title: "Computer Lab",
    description: "Up-to-date computer lab with high-speed internet and modern desktop systems.",
    path: "/facilities/computer-lab",
    icon: <RxDesktop size={24} />,
    accentColor: "#84cc16",
    borderColor: "rgba(132,204,22,0.2)",
    glowColor: "rgba(132,204,22,0.12)",
  },
  {
    title: "Smart Classes",
    description: "Interactive smart boards enabling digital multimedia teaching and engagement.",
    path: "/facilities/smart-class",
    icon: <LuMonitorSmartphone size={24} />,
    accentColor: "#ef4444",
    borderColor: "rgba(239,68,68,0.2)",
    glowColor: "rgba(239,68,68,0.12)",
  },
  {
    title: "School Library",
    description: "Extensive collection of books, periodicals, quiet reading rooms, and resources.",
    path: "/facilities/library",
    icon: <PiBookOpenText size={24} />,
    accentColor: "#8b5cf6",
    borderColor: "rgba(139,92,246,0.2)",
    glowColor: "rgba(139,92,246,0.12)",
  },
  {
    title: "Music Room",
    description: "Dedicated space for Indian & Western instruments, vocal training, and performance.",
    path: "/facilities/music-room",
    icon: <PiMusicNotesBold size={24} />,
    accentColor: "#f472b6",
    borderColor: "rgba(244,114,182,0.2)",
    glowColor: "rgba(244,114,182,0.12)",
  },
  {
    title: "Sports Activities",
    description: "Vibrant athletics, kabaddi, football, wrestling, and fitness curriculum.",
    path: "/facilities/sports",
    icon: <GiSoccerBall size={24} />,
    accentColor: "#06b6d4",
    borderColor: "rgba(6,182,212,0.2)",
    glowColor: "rgba(6,182,212,0.12)",
  },
  {
    title: "School Transportation",
    description: "Safe and reliable fleet of buses with GPS tracking, CCTV, and trained attendants.",
    path: "/facilities/transport",
    icon: <FaBus size={24} />,
    accentColor: "#f59e0b",
    borderColor: "rgba(245,158,11,0.2)",
    glowColor: "rgba(245,158,11,0.12)",
  },
  {
    title: "Extraordinary Campus",
    description: "Lush green campus, open-air stage, auditorium, reception, and healthcare facility.",
    path: "/facilities/campus-info",
    icon: <LuSchool size={24} />,
    accentColor: "#f4c842",
    borderColor: "rgba(244,200,66,0.2)",
    glowColor: "rgba(244,200,66,0.12)",
  },
];

const Facilities = () => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative min-h-screen pt-36 md:pt-40 pb-20 px-6 md:px-16 overflow-hidden bg-[#020810]"
    >
      {/* Ambient glows */}
      <div
        className="absolute top-20 left-0 w-96 h-96 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-20 right-0 w-72 h-72 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,200,66,0.05) 0%, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Page header */}
        <div className="pt-8 pb-12 text-center md:text-left">
          <span className="section-badge mb-5 inline-flex">Campus & Infrastructure</span>
          <h1
            className="uppercase tracking-wide"
            style={{
              fontFamily: "Orbitron, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              color: "#f0f5ff",
            }}
          >
            Our <span className="text-gold">Facilities</span>
          </h1>
          <div className="cosmic-divider w-48 mt-4 mx-auto md:mx-0" />
          <p className="text-gray-400 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">
            Galaxy Star Public School provides world-class educational spaces, state-of-the-art laboratories, recreational amenities, and secure campus transport.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {facilitiesData.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              onClick={() => navigate(item.path)}
              className="glass-card p-6 cursor-pointer group transition-all duration-300 flex flex-col justify-between"
              style={{ borderColor: item.borderColor }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 28px ${item.glowColor}, 0 8px 32px rgba(0,0,0,0.4)`;
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.borderColor = item.accentColor + "44";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = item.borderColor;
              }}
            >
              <div>
                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-105"
                  style={{
                    background: item.glowColor,
                    border: `1px solid ${item.borderColor}`,
                    color: item.accentColor,
                  }}
                >
                  {item.icon}
                </div>

                {/* Title */}
                <h3
                  className="font-bold mb-2 group-hover:text-gold transition-colors duration-200"
                  style={{
                    fontFamily: "Cinzel, serif",
                    fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)",
                    color: "#f0f5ff",
                    letterSpacing: "0.03em",
                  }}
                >
                  {item.title}
                </h3>

                {/* Description */}
                <p
                  className="text-xs sm:text-sm leading-relaxed mb-4"
                  style={{ fontFamily: "Nunito, sans-serif", color: "#8899b8" }}
                >
                  {item.description}
                </p>
              </div>

              {/* CTA */}
              <span
                className="text-xs font-bold tracking-wider uppercase flex items-center gap-1.5 transition-all duration-200 group-hover:gap-2.5 mt-2"
                style={{ fontFamily: "Nunito, sans-serif", color: item.accentColor }}
              >
                Explore Facility →
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default Facilities;
